import { useFormikContext } from 'formik'
import { useIntl } from 'react-intl'

import { BidInput } from '@b-stock/fe-core/bstock-next/components'
import { type OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  PrecisionNumberFormat,
  InputBordered,
} from '@b-stock/fe-core/bstock-react'

import { getIsPercentOffer } from '@/buyer-portal/components/features/listing/ListingActionColumn/actionCard/offer/useOfferAmount'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useOfferAnalytics } from '../useOfferAnalytics'
import { type MakeAnOfferFormValues } from './MakeAnOfferFormProvider'

export const SimpleOfferInput = ({
  listing,
  ns,
  disabled,
  shortUnit = false,
}: {
  listing: OrchestratedOfferingListingDetails
  ns: string
  disabled: boolean
  // Shared with the legacy V1 offer module, which keeps the long-form unit.
  shortUnit?: boolean
}) => {
  const { formatMessage } = useIntl()
  const { offering, contract } = listing
  const { values, setFieldValue, errors } =
    useFormikContext<MakeAnOfferFormValues>()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)

  const handleValueChange = async (value: number | null) => {
    await setFieldValue('amount', value ?? 0)
  }

  const perUnit = contract?.bidUnit
    ? formatMessage({
        id: shortUnit
          ? `Contracts.Enums.perUnit.shortSentenceCase.${contract.bidUnit}`
          : `Contracts.Enums.perUnit.${contract.bidUnit}`,
      })
    : ''

  const currencyCode = listing.currency ?? 'USD'

  const isPercent = getIsPercentOffer(listing)
  const bidBasis = contract?.bidBasis ?? (isPercent ? 'RELATIVE' : undefined)

  return (
    <>
      <BidInput
        allowFractionalCents={!!contract}
        bidBasis={bidBasis}
        bidPrecision={offering?.bidPrecision}
        currencyCode={currencyCode}
        disabled={disabled}
        error={!!errors.amount}
        forceShowCents
        handleValueChange={handleValueChange}
        label={
          <FormattedMessage
            id={`${ns}.inputLabel`}
            values={{
              perUnit,
              value: (
                <PrecisionNumberFormat
                  value={values.minOfferAmount}
                  precision={offering?.bidPrecision}
                  currency={currencyCode}
                  format={isPercent ? 'percent' : 'currency'}
                />
              ),
            }}
          />
        }
        onFocus={() =>
          trackButtonClicked({
            buttonName: 'offer_amt_field',
            ...getOfferAnalytics(),
          })
        }
        pricingStrategy="MAKE_AN_OFFER"
        storedValue={values.amount || null}
      />
      {errors.amount && (
        <InputBordered.ErrorMessage>{errors.amount}</InputBordered.ErrorMessage>
      )}
    </>
  )
}
